/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useState, useEffect, useCallback } from 'react';
import queryString from 'query-string';
import { useLocation, useHistory } from 'react-router';
import type { IProps } from './inbox-radar-plans-container';
import BillingSummaryModal from './components/billing-summary-modal';
import PaddleBillingSummaryModal from '../outreach-plans/components/paddle-billing-summary-modal/paddle-billing-summary-modal';
import { previewPaddleSubscription } from '../../helpers/billing-subscription.api';
import openPaddleCustomerPortal from '../../helpers/paddle-customer-portal';
import type { PaddlePreviewSubscriptionResponse } from '../../types/paddle';
import { executeOnRequestStatus } from '../../../../../../shared/utils/execute-on-request-status';
import {
  getIsInboxAnalyzerUserSubscribed,
  isRequestPending,
} from '../../utils/helper';
import PlansSection from './components/plans-section';
import { BillingOption } from '../../../../../../shared/utils/billing-options';
import { getDisabledLabel } from '../modify-subscription/utils/helper';
import { Plan } from '../../types';
import { SubscriptionPlans } from '../../../../../../shared/utils/subscription-plans';
import {
  getIsRequestPending,
  redirectWithoutRefresh,
} from '../../../../../../shared/utils';
import { showToastMessage } from '../../../../../../shared/design-system/components/molecules/upgrade-plan-billing-section/helper/helper';
import {
  getPlanIndex,
  getPlanPeriod,
  getSubscribeAction,
  UpgradePlanQueryParamsKey,
  isValueBasedPlan,
} from './utils/helper';
import { getPlanId } from '../upgrade-plan/utils/helper';
import { Routes } from '../../../../../../shared/routes';
import PlanCycleSwitch from '../../../../../../shared/design-system/components/molecules/plan-cycle-switch';
import { getInboxRadarPlanMetaData } from './components/inbox-radar-plan-card/utils/helper';
import toaster from '../../../../../../shared/toaster';
import { showGeneralErrorNotification } from '../../../../../../shared/utils/errors';
import usePaddleIntegration from '../../../../../../shared/hooks/script-integrations/usePaddleIntegration';

const MANUALLY_MODIFY_SUBSCRIPTION_MSG =
  'Your subscription was created manually. Please contact the support team to modify your subscription.';
import usePaddleCoupon from '../../hooks/use-paddle-coupon';
import { ShAccountSettingsCode } from '../../../../enums/admin-settings';

const InboxRadarPlans: React.FC<IProps> = ({
  //* API Request State
  getCouponCodeResponse,
  sendGetInboxAnalyzerSubscriptionRequestStatus,
  sendGetInboxAnalyzerSubscriptionResponse,
  sendGetInboxAnalyzerPlansResponse,
  sendPurchaseInboxAnalyzerSubscriptionRequestStatus,
  sendPurchaseInboxAnalyzerSubscriptionRequestMessage,
  sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus,
  resetPurchaseInboxAnalyzerModifySubscriptionResponse,
  sendPurchaseInboxAnalyzerModifySubscriptionResponse,
  //* API Request
  sendPurchaseInboxAnalyzerSubscriptionRequest,
  sendPurchaseInboxAnalyzerModifySubscriptionRequest,
  sendGetCalculateInboxAnalyzerPayRequest,
  sendGetInboxAnalyzerSubscriptionRequest,
  sendGetUserSettingsRequest,
  sendGetUserSubscriptionMetadataRequest,
  resetSubscription,

  paymentProvider,
  userId,
  shAccountId,
  userEmail,
  paddlePolling,
  startPaddlePollingThunk,
  resetPurchaseInboxAnalyzerSubscriptionRequestStatus,
  adminSettings,
  paddleCustomerId,
  sendGetPostLoadMetaRequest,
}) => {
  const location = useLocation();
  const history = useHistory();

  //* States
  const [planCycle, setPlanCycle] = useState<BillingOption>(
    BillingOption.ANNUALLY,
  );
  const isPurchaseInProgress =
    isRequestPending(sendPurchaseInboxAnalyzerSubscriptionRequestStatus) ||
    isRequestPending(
      sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus,
    );
  const [
    isBillingSummaryModalShow,
    setIsBillingSummaryModalShow,
  ] = useState<boolean>(false);
  const [isUserModifyingSubscription, setIsModifySubscription] = useState(
    false,
  );
  const [selectedPlanDetails, setSelectedPlanDetails] = useState<Plan>();

  const [
    isPaddleBillingSummaryModalShow,
    setIsPaddleBillingSummaryModalShow,
  ] = useState(false);
  const [
    paddlePreviewData,
    setPaddlePreviewData,
  ] = useState<PaddlePreviewSubscriptionResponse | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const {
    isCouponApplying,
    isCouponApplied,
    isCouponInvalid,
    preAppliedCouponCode,
    onApplyCoupon: onPaddleApplyCoupon,
    onClearCoupon: onPaddleClearCoupon,
    resetCouponState,
  } = usePaddleCoupon({
    planId: selectedPlanDetails?.id,
    paddlePriceId: selectedPlanDetails?.paddlePlanId,
    application: 'inbox-analyzer',
    shAccountId,
    onPreviewUpdate: setPaddlePreviewData,
  });

  const isFreePlanPaddleUser =
    !isUserModifyingSubscription && paymentProvider === 'paddle';

  const INBOX_RADAR_SUBSCRIPTION_URL =
    '/billing/subscriptions/inbox-placement-radar';

  const isPaddlePolling = paddlePolling?.isActive && paddlePolling?.applicationType === 'inbox-analyzer';

  const handleCheckoutStateReset = useCallback(() => {
    setIsPaddleBillingSummaryModalShow(false);
    setPaddlePreviewData(null);
    resetCouponState();
  }, [resetCouponState]);

  const { isReady: isPaddleReady, openCheckout } = usePaddleIntegration({
    onCheckoutComplete: () => {
      if (window.Paddle?.Checkout?.close) {
        window.Paddle.Checkout.close();
      }
      handleCheckoutStateReset();
      redirectWithoutRefresh(INBOX_RADAR_SUBSCRIPTION_URL);
      startPaddlePollingThunk({
        shAccountId,
        applicationType: 'inbox-analyzer',
        planId: selectedPlanDetails?.id,
      });
    },
    onCheckoutError: () => {
      handleCheckoutStateReset();
    },
    onCheckoutPaymentError: () => {
      handleCheckoutStateReset();
      toaster.error('Payment failed. Please try again.');
      redirectWithoutRefresh(INBOX_RADAR_SUBSCRIPTION_URL);
    },
    onError: () => {
      toaster.error('Something went wrong. Please try again.');
    },
    onCheckoutClosed: () => {
      handleCheckoutStateReset();
    },
  });

  const showBillingSummaryModal = () => setIsBillingSummaryModalShow(true);
  const hideBillingSummaryModal = () => {
    setIsBillingSummaryModalShow(false);
    resetQueryParams();
    resetCouponState();
  };

  const handelBillingOption = (value) => setPlanCycle(value);

  const onSelectPlan = async (planId: number, planName: string) => {
    if (isPaddlePolling) return;
    const { planCode } = sendGetInboxAnalyzerSubscriptionResponse;

    const selectedPlan = sendGetInboxAnalyzerPlansResponse.find(
      ({ id }) => id === planId,
    );

    setSelectedPlanDetails(selectedPlan);

    if (
      getIsInboxAnalyzerUserSubscribed(planCode) &&
      paymentProvider !== 'paddle'
    ) {
      sendGetCalculateInboxAnalyzerPayRequest({
        accountSubscriptionPlanId: planId,
        slots: 1,
      });
    }

    if (isFreePlanPaddleUser && selectedPlan?.paddlePlanId && isPaddleReady) {
      openCheckout({
        items: [{ priceId: selectedPlan.paddlePlanId, quantity: 1 }],
        customer: {
          ...(paddleCustomerId
            ? { id: paddleCustomerId }
            : { email: userEmail }),
        },
        customData: {
          user_id: userId,
          sh_account_id: shAccountId,
        },
        settings: {
          displayMode: 'overlay',
          theme: 'light',
          ...(paddleCustomerId && { allowLogout: false }),
        },
      });
      return;
    }

    if (paymentProvider === 'paddle') {
      setIsPreviewLoading(true);
      setIsPaddleBillingSummaryModalShow(true);
      try {
        const response = await previewPaddleSubscription({
          accountSubscriptionPlanId: planId,
          application: 'inbox-analyzer',
          shAccountId,
          // ...(couponCodeToApply && { couponCode: couponCodeToApply }),
        });
        setPaddlePreviewData(response);
      } catch {
        toaster.error('Failed to load billing preview. Please try again.');
        setIsPaddleBillingSummaryModalShow(false);
      } finally {
        setIsPreviewLoading(false);
      }
    } else {
      showBillingSummaryModal();
    }
    updateQueryParams(planId);
  };

  const onPurchase = (token, couponCode) => {
    if (!isUserModifyingSubscription) {
      const payload = {
        slots: 1,
        planId: Number(selectedPlanDetails?.id),
        ...(token && { stripeCardToken: token.id }),
        ...(getCouponCodeResponse &&
          !getCouponCodeResponse.isError &&
          couponCode && { couponCode }),
      };
      sendPurchaseInboxAnalyzerSubscriptionRequest(payload);
    } else {
      const { customerId, subscriptionId } = sendGetInboxAnalyzerSubscriptionResponse;
      if (!customerId || !subscriptionId) {
        showGeneralErrorNotification(MANUALLY_MODIFY_SUBSCRIPTION_MSG);
        return;
      }
      const payload = {
        accountSubscriptionPlanId: Number(selectedPlanDetails?.id),
        slots: 1,
        stripeCardToken: token.id,
        ...(getCouponCodeResponse &&
          !getCouponCodeResponse.isError &&
          couponCode && { couponCode }),
      };
      sendPurchaseInboxAnalyzerModifySubscriptionRequest(payload);
    }
  };

  const onPaddlePayNow = () => {
    if (!paddleCustomerId) {
      toaster.error('Unable to modify subscription. Please contact support.');
      return;
    }
    sendPurchaseInboxAnalyzerModifySubscriptionRequest({
      accountSubscriptionPlanId: Number(selectedPlanDetails?.id),
      ...(preAppliedCouponCode && { couponCode: preAppliedCouponCode }),
    });
  };

  const onUpdateCardDetails = () => {
    const {
      customerId,
      subscriptionId,
    } = sendGetInboxAnalyzerSubscriptionResponse;
    if (!customerId || !subscriptionId) {
      showGeneralErrorNotification(MANUALLY_MODIFY_SUBSCRIPTION_MSG);
      return;
    }
    openPaddleCustomerPortal({ customerId, subscriptionId });
  };

  const hidePaddleBillingSummaryModal = () => {
    setIsPaddleBillingSummaryModalShow(false);
    setPaddlePreviewData(null);
    resetQueryParams();
    resetCouponState();
  };

  const sendPurchaseSubscriptionRequestSuccess = (message) => {
    showToastMessage(message);

    sendGetInboxAnalyzerSubscriptionRequest();
    redirectWithoutRefresh(
      '/billing/subscriptions/inbox-placement-radar?plan=subscribed',
    );
    resetSubscription();

    setTimeout(() => {
      // Stripe is taking time to update payment is pending or not data
      sendGetUserSettingsRequest();
      sendGetUserSubscriptionMetadataRequest();
      sendGetPostLoadMetaRequest();
    }, 500);
  };

  const updateQueryParams = (planId) => {
    const queryParams = new URLSearchParams(location.search);
    const index = getPlanIndex(sendGetInboxAnalyzerPlansResponse, planId);
    const selectPlanIndex = index < 3 ? index + 1 : index - 2;

    queryParams.set(
      UpgradePlanQueryParamsKey.Action,
      getSubscribeAction(isUserModifyingSubscription),
    );
    queryParams.set(UpgradePlanQueryParamsKey.Period, getPlanPeriod(planCycle));
    queryParams.set(UpgradePlanQueryParamsKey.Plan, selectPlanIndex.toString());

    history.push({
      pathname: Routes.Setting_Billing_Inbox_Placement_Radar,
      search: queryParams.toString(),
    });
  };

  const resetQueryParams = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete(UpgradePlanQueryParamsKey.Action);
    queryParams.delete(UpgradePlanQueryParamsKey.Period);
    queryParams.delete(UpgradePlanQueryParamsKey.Plan);

    history.push({
      pathname: Routes.Setting_Billing_Inbox_Placement_Radar,
      search: queryParams.toString(),
    });
  };

  const selectPlanBasedOnUrl = () => {
    const {
      planName,
      planType,
      planCode,
    } = sendGetInboxAnalyzerSubscriptionResponse;
    const params = queryString.parse(location.search);
    const { plan, period } = params;

    if (sendGetInboxAnalyzerPlansResponse) {
      const upgradePlan = sendGetInboxAnalyzerPlansResponse.filter(
        ({ planMode, planType: type }) =>
          Number(period) === planMode && isValueBasedPlan(type),
      )[Number(plan) - 1];

      const subscriptionId = getPlanId(
        sendGetInboxAnalyzerPlansResponse,
        planName,
        planCode,
        planType,
      );

      if (
        upgradePlan?.id &&
        (upgradePlan?.id > subscriptionId ||
          planCode === SubscriptionPlans.FREE ||
          planCode === SubscriptionPlans.TRIAL)
      ) {
        onSelectPlan(upgradePlan?.id, upgradePlan?.planName);
      }
    }
  };

  const getActivePlanName = () => {
    const { planName } = sendGetInboxAnalyzerSubscriptionResponse;
    const emailOutreachPlanMetaData = getInboxRadarPlanMetaData(planName);

    return emailOutreachPlanMetaData?.displayName || planName;
  };

  // const getPremiumFeatureTooltipText = (): string =>
  //   'A/Z variant \nPipedrive Integration \nTeam Management \nAPI \nSender Rotation \nUnified Inbox Reply';

  //* useEffect
  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetInboxAnalyzerSubscriptionRequestStatus,
      onSuccess: () => {
        const { planCode } = sendGetInboxAnalyzerSubscriptionResponse;

        const isUserSubscribed = getIsInboxAnalyzerUserSubscribed(planCode);
        setIsModifySubscription(isUserSubscribed);

        selectPlanBasedOnUrl();
      },
    });
  }, [sendGetInboxAnalyzerSubscriptionRequestStatus]);

  // Purchase subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendPurchaseInboxAnalyzerSubscriptionRequestStatus,
      onSuccess: () => {
        sendPurchaseSubscriptionRequestSuccess(
          sendPurchaseInboxAnalyzerSubscriptionRequestMessage,
        );
        resetPurchaseInboxAnalyzerSubscriptionRequestStatus();
      },
    });
  }, [sendPurchaseInboxAnalyzerSubscriptionRequestStatus]);

  // Modify subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus,
      onSuccess: () => {
        if (paymentProvider === 'paddle') {
          hidePaddleBillingSummaryModal();
          redirectWithoutRefresh(INBOX_RADAR_SUBSCRIPTION_URL);
          startPaddlePollingThunk({
            shAccountId,
            applicationType: 'inbox-analyzer',
            planId: selectedPlanDetails?.id,
          });
        } else {
          sendPurchaseSubscriptionRequestSuccess(
            sendPurchaseInboxAnalyzerModifySubscriptionResponse?.message,
          );
        }
        resetPurchaseInboxAnalyzerModifySubscriptionResponse();
      },
    });
  }, [sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus]);

  return (
    <div className="inbox-radar-plans">
      <div className="inbox-radar-plans-header">
        <h1>Find the right Inbox Placement Radar Plan</h1>
        <span>
          You are currently on the{' '}
          <span className="semibold-1">{getActivePlanName()} plan</span>
        </span>
      </div>

      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ background: '#f9fafb', height: '43px', paddingTop: '18px' }}
      >
        <div className="d-flex flex-column align-items-end mt-4">
          <PlanCycleSwitch
            value={planCycle}
            onChange={handelBillingOption}
            disabledLabel={getDisabledLabel(
              sendGetInboxAnalyzerSubscriptionResponse,
            )}
          />
        </div>
      </div>

      <div className="d-flex justify-content-center">
        <PlansSection
          onSelectPlan={onSelectPlan}
          planCycle={planCycle}
          selectedPlanId={selectedPlanDetails?.id}
          isPaddlePolling={isPaddlePolling}
        />
      </div>

      <BillingSummaryModal
        show={isBillingSummaryModalShow}
        onClose={hideBillingSummaryModal}
        selectedPlanDetails={selectedPlanDetails}
        onPurchase={onPurchase}
        isUserModifyingSubscription={isUserModifyingSubscription}
        isAnnuallyCycle={planCycle === BillingOption.ANNUALLY}
      />

      <PaddleBillingSummaryModal
        show={isPaddleBillingSummaryModalShow}
        onClose={hidePaddleBillingSummaryModal}
        planName={selectedPlanDetails?.planName || ''}
        previewData={paddlePreviewData}
        onPayNow={onPaddlePayNow}
        isPurchaseInProgress={isPurchaseInProgress}
        isPreviewLoading={isPreviewLoading}
        onUpdateCardDetails={onUpdateCardDetails}
        onApplyCoupon={onPaddleApplyCoupon}
        onClearCoupon={onPaddleClearCoupon}
        isCouponApplying={isCouponApplying}
        isCouponApplied={isCouponApplied}
        isCouponInvalid={isCouponInvalid}
      />
    </div>
  );
};

export default InboxRadarPlans;
